import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { createPopper } from '@popperjs/core';
import { AnimatePresence, motion } from 'framer-motion';
import Overflow from '../../Icons/Overflow';
import styles from './OverflowMenu.module.scss';

const POPPER_OPTIONS = {
  placement: 'bottom-end',
  strategy: 'fixed',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 8],
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-end', 'bottom-start', 'top-start'],
      },
    },
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        padding: 8,
      },
    },
  ],
};

// Opacity-only on the Popper node — avoids overwriting Popper's `transform` for placement.
const menuMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
};

function OverflowMenu({ options, buttonContent }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const popperInstanceRef = useRef(null);

  // Create Popper when menu opens; destroy in onExitComplete so exit animation keeps position.
  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current || !menuRef.current) return undefined;

    popperInstanceRef.current?.destroy();
    popperInstanceRef.current = createPopper(
      buttonRef.current,
      menuRef.current,
      POPPER_OPTIONS
    );

    return undefined;
  }, [isOpen]);

  const destroyPopper = () => {
    if (popperInstanceRef.current) {
      popperInstanceRef.current.destroy();
      popperInstanceRef.current = null;
    }
  };

  // Close menu on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close menu on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const canPortal = typeof document !== 'undefined' && document.body;
  const menuNode = (
    <AnimatePresence onExitComplete={destroyPopper}>
      {isOpen && (
        <motion.div
          key="overflow-menu"
          ref={menuRef}
          className={styles.menu}
          {...menuMotion}
        >
          {options.map((option, idx) => (
            <button
              key={idx}
              className={`${styles.menuItem} ${option.isDestructive ? styles.destructive : ''}`}
              onClick={() => {
                setIsOpen(false);
                option.handler && option.handler();
              }}
              aria-label={option.label}
            >
              {option.icon && <span className={styles.icon}>{option.icon}</span>}
              {option.label}
            </button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className={styles.overflowMenu}>
      <button
        ref={buttonRef}
        className={styles.overflowButton}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {buttonContent || <Overflow />}
      </button>
      {canPortal ? createPortal(menuNode, document.body) : menuNode}
    </div>
  );
}

export default OverflowMenu;
